/**
 * A small Markdown renderer for assistant replies: headings, paragraphs, lists, tables, code, bold, italics and
 * links. It builds React elements rather than HTML strings, so model output can never inject markup.
 */
import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';

export function Markdown({ text }: { text: string }) {
  return <div className="prose-chat">{blocks(text)}</div>;
}

function blocks(src: string): ReactNode[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  const key = () => out.length;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    // Fenced code
    if (line.trimStart().startsWith('```')) {
      const body: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trimStart().startsWith('```')) body.push(lines[i++]!);
      i++;
      out.push(<pre key={key()}>{body.join('\n')}</pre>);
      continue;
    }
    // Headings
    const h = /^(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      const content = inline(h[2]!);
      out.push(level === 1 ? <h1 key={key()}>{content}</h1> : level === 2 ? <h2 key={key()}>{content}</h2> : <h3 key={key()}>{content}</h3>);
      i++;
      continue;
    }
    // Tables: a header row followed by a |---| separator
    if (line.includes('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1]!)) {
      const head = cells(line);
      const align = cells(lines[i + 1]!).map((c) => (c.endsWith(':') ? 'right' : 'left') as 'right' | 'left');
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && lines[i]!.includes('|') && lines[i]!.trim()) rows.push(cells(lines[i++]!));
      out.push(
        <div key={key()} className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                {head.map((c, j) => (
                  <th key={j} style={{ textAlign: align[j] ?? 'left' }}>
                    {inline(c)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, k) => (
                <tr key={k}>
                  {head.map((_, j) => (
                    <td key={j} style={{ textAlign: align[j] ?? (looksNumeric(r[j]) ? 'right' : 'left') }}>
                      {inline(r[j] ?? '')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    // Lists
    const bullet = /^\s*[-*•]\s+/;
    const ordered = /^\s*\d+[.)]\s+/;
    if (bullet.test(line) || ordered.test(line)) {
      const isOrdered = ordered.test(line);
      const re = isOrdered ? ordered : bullet;
      const items: string[] = [];
      while (i < lines.length && (re.test(lines[i]!) || (items.length && /^\s{2,}\S/.test(lines[i]!)))) {
        if (re.test(lines[i]!)) items.push(lines[i]!.replace(re, ''));
        else items[items.length - 1] += ` ${lines[i]!.trim()}`;
        i++;
      }
      const lis = items.map((t, k) => <li key={k}>{inline(t)}</li>);
      out.push(isOrdered ? <ol key={key()}>{lis}</ol> : <ul key={key()}>{lis}</ul>);
      continue;
    }
    // Paragraph: until a blank line or another block starts
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,6}\s|```|\s*[-*•]\s|\s*\d+[.)]\s)/.test(lines[i]!)) {
      if (para.length && lines[i]!.includes('|') && i + 1 < lines.length && /^\s*\|?\s*:?-{2,}/.test(lines[i + 1]!)) break;
      para.push(lines[i++]!);
    }
    if (!para.length) para.push(lines[i++]!);
    out.push(
      <p key={key()}>
        {para.map((p, k) => (
          <Fragment key={k}>
            {k > 0 && <br />}
            {inline(p)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return out;
}

function cells(row: string) {
  return row
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((c) => c.trim());
}

function looksNumeric(s: string | undefined) {
  return !!s && /^[-+(]?[$£€₹]?\s?[\d,.]+%?\)?[kKmMbB]?$/.test(s.replace(/\*\*/g, '').trim());
}

/** Inline spans: `code`, **bold**, *italic*, [text](href). In-app paths become router links; others open safely. */
function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*\s][^*]*\*)|(\[[^\]]+\]\([^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const t = m[0];
    const k = out.length;
    if (m[1]) out.push(<code key={k}>{t.slice(1, -1)}</code>);
    else if (m[2]) out.push(<strong key={k}>{inline(t.slice(2, -2))}</strong>);
    else if (m[3]) out.push(<em key={k}>{t.slice(1, -1)}</em>);
    else {
      const [, label, href] = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(t)!;
      if (href!.startsWith('/')) {
        out.push(
          <Link key={k} to={href!} className="text-brand underline">
            {label}
          </Link>,
        );
      } else if (/^https?:\/\//.test(href!)) {
        out.push(
          <a key={k} href={href} target="_blank" rel="noreferrer noopener" className="text-brand underline">
            {label}
          </a>,
        );
      } else out.push(label);
    }
    last = m.index + t.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
